import React, { ReactNode, ElementType, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import styled from 'styled-components';

import { WithClickOutside } from './withClickOutside';
import { colorScheme } from '../../theme';
import { useEventListener, emitEvent } from '../../hooks/useEventListener';

interface Props extends React.HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  stayOpenAfterClick?: boolean;
  as?: ElementType;
  toggleContent?: ReactNode;
}

const CustomToggle = React.forwardRef((props: any, ref) => {
  const { onClick, show, setShow, asProps } = props;
  return (
    <KebabIcon
      ref={ref as any}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
        if (setShow) {
          setShow(!show);
        }
      }}
      data-test="kebab"
      aria-label="More options"
      aria-controls="kebab-menu"
      data-dd-action-name={asProps?.['data-dd-action-name']}
    >
      &#x25CF;&#x25CF;&#x25CF;
    </KebabIcon>
  );
});

const CustomAsToggle = React.forwardRef((props: any, ref) => {
  const { onClick, AsComponent, asProps, toggleContent } = props;

  if (!AsComponent) {
    return <CustomToggle onClick={onClick} ref={ref} {...props} />;
  }

  return (
    <AsComponent
      ref={ref}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(e);
      }}
      {...asProps}
    >
      {toggleContent}
    </AsComponent>
  );
});

export function Kebab(props: Props) {
  const [show, setShow] = React.useState(false);
  // Unique ID used to coordinate multiple Kebab instances - when one opens, others close
  const kebabId = React.useId();
  const { children, stayOpenAfterClick, as: AsComponent, toggleContent, ...asProps } = props;

  const handleOtherKebabOpen = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.id !== kebabId) {
        setShow(false);
      }
    },
    [kebabId],
  );

  useEventListener('kebab_open_event', handleOtherKebabOpen);

  const handleToggle = (nextShow: boolean) => {
    setShow(nextShow);
    if (nextShow) {
      emitEvent('kebab_open_event', { id: kebabId });
    }
  };

  const clickOutside = () => {
    setShow(false);
  };

  const customAsToggleProps = {
    AsComponent,
    asProps,
    show,
    setShow: handleToggle,
    toggleContent,
  };

  if (stayOpenAfterClick) {
    return (
      <WithClickOutside onClickOutside={clickOutside}>
        <StyledDropdown show={show}>
          <Dropdown.Toggle as={AsComponent ? CustomAsToggle : CustomToggle} {...customAsToggleProps} />
          <StyledMenu id="kebab-menu">{children}</StyledMenu>
        </StyledDropdown>
      </WithClickOutside>
    );
  }

  return (
    <StyledDropdown onToggle={handleToggle} show={show}>
      <Dropdown.Toggle as={AsComponent ? CustomAsToggle : CustomToggle} {...customAsToggleProps} />
      <StyledMenu id="kebab-menu">{children}</StyledMenu>
    </StyledDropdown>
  );
}

const StyledMenu = styled(Dropdown.Menu)`
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1);
`;

const KebabIcon = styled.span.attrs({ tabIndex: 0, role: 'button', ariaLabel: 'More options' })`
  display: inline-block;
  color: ${colorScheme.text.base};
  opacity: 0.85;
  cursor: pointer;
  font-size: 0.5em;
  letter-spacing: 1px;
  line-height: 1.6rem;
  vertical-align: middle;
`;

const StyledDropdown = styled(Dropdown)`
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: end;
  background: transparent;
  border-color: ${colorScheme.card.border};
`;
